import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

// import {
//   type LeadUpdateRequest,
//   leadUpdateRequestSchema,
// } from "@pbz/schema/lead";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "@pbz/ui-kit";

import { FormLabel } from "@/components/FormLabel";
import { useUpdateLeadDetails } from "@/queries/lead";
import {
  type ApplicationReferenceNumberFormData,
  applicationReferenceNumberSchema,
} from "@/schema/lead";

interface ApplicationReferenceNumberProps {
  leadId: number;
  onSuccess?: (arnNumber: string) => void;
  defaultValues?: ApplicationReferenceNumberFormData;
}

const ApplicationReferenceNumber: React.FC<ApplicationReferenceNumberProps> = ({
  leadId,
  onSuccess,
  defaultValues,
}) => {
  const form = useForm<ApplicationReferenceNumberFormData>({
    resolver: zodResolver(applicationReferenceNumberSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      leadId,
      arnNumber: "",
      ...(defaultValues || {}),
    },
  });

  // Update Lead Details mutation
  const updateLeadDetailsMutation = useUpdateLeadDetails({
    onSuccess: (data) => {
      toast.success(
        data.message || "Application reference number saved successfully",
      );
      const arnNumber = form.getValues("arnNumber");
      onSuccess?.(arnNumber || "");
    },
    onError: (error) => {
      toast.error(
        error.message ||
          "Failed to save application reference number. Please try again.",
      );
    },
  });

  const handleFormSubmit = () => {
    const formData = form.getValues();
    updateLeadDetailsMutation.mutate(formData);
  };

  return (
    <Card className="mb-10 bg-white">
      <CardHeader>
        <CardTitle>Application Reference Number</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Application Number Input */}
              <FormField
                control={form.control}
                name="arnNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Application Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Application Number"
                        {...field}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Save & Submit Button */}
            <div className="flex justify-end">
              <Button
                className=" bg-blue-600 text-white hover:bg-blue-700"
                disabled={updateLeadDetailsMutation.isPending}
                type="submit"
              >
                {updateLeadDetailsMutation.isPending
                  ? "Saving..."
                  : "Save & Submit"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ApplicationReferenceNumber;
